import type { EditorAiUsageStatus } from './editor-ai';
import type { EditorAiCreditMeter } from './editor-ai-ui';

/**
 * The results AI (P-PER-44): a chat on an experiment's Statistics tab that explains the result.
 * One conversation per experiment, saved and shared by everyone with access to it (decided
 * 2026-10-20, before launch), kept RESULTS_AI_RETENTION_MS. The server builds the model's history
 * from the stored turns; the browser sends only the new question and the view.
 */

/** How long a turn is kept: 12 months, then the daily cleanup deletes it. */
export const RESULTS_AI_RETENTION_MS = 365 * 24 * 60 * 60 * 1000;

/** The newest turns GET /thread returns. Older ones are kept, just not shown. */
export const RESULTS_AI_THREAD_LIMIT = 200;

/** The first-use notice, acknowledged through user_acknowledgements under its own key. */
export const RESULTS_AI_NOTICE_KEY = 'results-ai';
/**
 * Raised whenever the notice says something new, so everyone accepts what it says now. 2
 * (2026-10-20): it names the settings, brief and debrief sent along with the results. 3 (2026-10-20):
 * the website's learnings go too, when the website has them on.
 */
export const RESULTS_AI_NOTICE_VERSION = 3;

/** The ledger feature, and the prefix of each turn's ref (`results-ai:<turnId>`). */
export const RESULTS_AI_CREDIT_FEATURE = 'results-ai';

/** What the Statistics tab can be set to; the turn reads the same slice of the numbers. */
export const RESULTS_AI_GOALS = ['Conversions', 'Revenue'] as const;
export const RESULTS_AI_METHODS = ['frequentist', 'bayesian'] as const;
export const RESULTS_AI_CORRECTIONS = ['holm-bonferroni', 'benjamini-hochberg', 'sidak', 'none'] as const;

export type ResultsAiGoal = (typeof RESULTS_AI_GOALS)[number];
export type ResultsAiMethod = (typeof RESULTS_AI_METHODS)[number];
export type ResultsAiCorrection = (typeof RESULTS_AI_CORRECTIONS)[number];

/**
 * The question is hand-checked on the server (validate-chat-body.ts). The history limits are the
 * server's own, for the stored turns it replays to the model: they are resent, and paid for, on
 * every turn.
 */
export const RESULTS_AI_LIMITS = {
  messageChars: 2_000,
  /** Earlier messages replayed to the model, user and assistant together (ten exchanges). */
  historyMessages: 20,
  /** One earlier message: the assistant's answers run longer than a question. */
  historyMessageChars: 8_000,
  /** All earlier messages together. */
  historyChars: 60_000,
  /** The whole request body, checked before it is parsed. See body-parser. */
  requestBytes: 16 * 1024,
} as const;

/** How long a turn may run. A plain answer with no tools; well inside the editor's 240 s. */
export const RESULTS_AI_TURN_TIMEOUT_MS = 120_000;

/** The view the person is looking at. The currency is a key of the stats ('nill' is all of them). */
export type ResultsAiView = {
  currency: string;
  goal: ResultsAiGoal;
  method: ResultsAiMethod;
  correction: ResultsAiCorrection;
};

export type ResultsAiHistoryMessage = { role: 'user' | 'assistant'; text: string };

export type ResultsAiChatBody = {
  experimentId: number;
  message: string;
  view: ResultsAiView;
};

/** One stored turn as GET /thread answers it. Who asked is a key into the response's `authors`. */
export type ResultsAiThreadTurn = {
  turnId: string;
  question: string;
  answer: string;
  status: EditorAiUsageStatus;
  errorMessage: string | null;
  /** Asking again could help (a transient failure): the panel offers Try again. */
  errorRetryable: boolean;
  senderId: number;
  view: ResultsAiView;
  /** The stats have been recalculated since this answer read them. */
  stale: boolean;
  /** What the turn was charged (results_ai_usage), or null when its usage row is missing. */
  credits: number | null;
  createdAt: number;
};

/**
 * GET /api/results-ai/thread. Generic over the author because this package cannot import the
 * database's UserSummary; the server and the dashboard both fill it with that. Each person is sent
 * once: a summary carries the avatar as a data URL of up to about 100 KB, as in the editor's thread.
 */
export type ResultsAiThreadResponse<Author> = {
  /** Oldest first, the newest RESULTS_AI_THREAD_LIMIT. */
  turns: ResultsAiThreadTurn[];
  /** By user id. Someone whose account is gone is absent. */
  authors: Record<string, Author>;
  /**
   * A question someone is asking now: one runs at a time per experiment. `mine` when it is the
   * caller's own (another tab, or one still being saved after Stop), so the panel never says
   * "<your own name> is asking".
   */
  running: { senderId: number; startedAt: number; mine: boolean } | null;
};

/**
 * The SSE events of POST /api/results-ai/chat, in order: `turn`, then `status` steps and the
 * answer as `text` deltas, live `usage` (credits so far), and `error` before `done` when it failed.
 */
export type ResultsAiStreamEvent =
  | { type: 'turn'; turnId: string }
  | { type: 'status'; turnId: string; step: string }
  | { type: 'text'; turnId: string; text: string }
  | { type: 'usage'; turnId: string; credits: number }
  | { type: 'error'; turnId: string; message: string; retryable: boolean }
  | { type: 'done'; turnId: string; status: EditorAiUsageStatus };

/** GET /api/results-ai/status: whether the assistant is on, and whether this user accepted the notice. */
export type ResultsAiStatusResponse = { available: boolean; acknowledged: boolean };

/**
 * GET /api/results-ai/usage?experimentId=: the meter of the account that pays for the experiment's
 * turns, shaped as the editor's (one monthly cap and one ledger serve both). billingUrl only for an
 * account on credits, the one that can buy a pack.
 */
export type ResultsAiUsageResponse = { credits: EditorAiCreditMeter | null; billingUrl: string | null };
